// src/components/ui/Toaster.tsx
import { createContext, useContext, useState, useCallback } from 'react';

/** Optional single button on a toast (e.g. "Retry now"). Clicking it also dismisses the toast. */
export type ToastAction = { label: string; onClick: () => void; testId?: string };
type Toast = {
  id: string;
  title: string;
  description?: string;
  action?: ToastAction;
  testId?: string;
  /**
   * Text the user may need to copy by hand (shown selectable and pre-selected). Used by
   * useCopyToClipboard when the browser blocks both clipboard paths. A toast with copyText stays up
   * until the user dismisses it.
   */
  copyText?: string;
};
type ToastInput = Omit<Toast, 'id'>;

const ToastCtx = createContext<{ push: (t: ToastInput) => void }>({ push: () => {} });

let toastSeq = 0;
// crypto.randomUUID only exists in secure contexts (https / localhost), which is exactly where a
// clipboard failure toast is most likely to be needed; a counter is unique enough for React keys.
const nextToastId = () => `toast-${Date.now()}-${(toastSeq += 1)}`;

/** Select the whole fallback text as soon as it mounts, so Ctrl/Cmd+C works straight away. */
function selectContents(el: HTMLElement | null) {
  if (!el) return;
  try {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.selectAllChildren(el);
  } catch {
    /* selection is a convenience; the text is still visible and selectable */
  }
}

export function ToasterProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const dismiss = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const push = useCallback(
    (t: ToastInput) => {
      const id = nextToastId();
      setItems((prev) => [...prev, { id, ...t }]);
      // A copy fallback stays until dismissed; a toast with a button stays long enough to be read and pressed.
      if (!t.copyText) setTimeout(() => dismiss(id), t.action ? 9000 : 3500);
    },
    [dismiss]
  );
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      {/* Above dialogs (overlay z-100, content z-101): a toast fired from inside a dialog, such as
          "Link copied", used to render underneath the dialog's dimmed overlay. data-app-toaster lets
          DialogContent ignore clicks here instead of treating them as "click outside, close". */}
      <div
        className="fixed bottom-4 right-4 left-4 sm:left-auto z-[150] space-y-2 pointer-events-none flex flex-col items-end"
        role="status"
        aria-live="polite"
        data-app-toaster=""
      >
        {items.map((i) => (
          <div
            key={i.id}
            className="pointer-events-auto bg-[var(--surface-card)] border border-[var(--border-default)] shadow-lg rounded-lg p-3 w-full sm:w-80 animate-in fade-in slide-in-from-right-2"
            data-testid={i.copyText ? 'copy-fallback-toast' : i.testId || 'toast'}
          >
            <div className="font-semibold text-[var(--text-primary)]">{i.title}</div>
            {i.description && <div className="text-sm text-[var(--text-secondary)]">{i.description}</div>}
            {i.copyText != null && (
              <>
                <div
                  ref={selectContents}
                  className="mt-2 max-h-32 overflow-auto select-all whitespace-pre-wrap break-all rounded border border-[var(--border-default)] bg-[var(--surface-muted)] p-2 font-mono text-xs text-[var(--text-primary)]"
                  data-testid="copy-fallback-text"
                >
                  {i.copyText}
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]"
                    onClick={() => dismiss(i.id)}
                    data-testid="toast-dismiss"
                  >
                    Dismiss
                  </button>
                </div>
              </>
            )}
            {i.action && (
              <button
                type="button"
                className="mt-2 min-h-[2.25rem] rounded-md border border-[var(--border-strong)] px-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-muted)]"
                data-testid={i.action.testId}
                onClick={() => {
                  dismiss(i.id);
                  i.action?.onClick();
                }}
              >
                {i.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToaster = () => useContext(ToastCtx);
